import { useMemo } from 'react';
import {
  CameraIcon,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Router as RouterIconLucide,
  Server,
  Plus as PlusIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import type {
  RouterCheckKind,
  RouterCheckResponse,
  RouterHostDesignation,
  RouterProbeForward,
  RouterProbeHostKind,
} from '@/api/zoneRouter';
import { SectionLabel } from './RouterPanel';
import { HostDesignationMenu } from './HostDesignationMenu';
import { Tooltip } from '@/components/ui/tooltip';

/**
 * Group B's per-host import + designation list. Designed as the shared
 * primitive Group D will lift into `LocalComputerPanel.tsx` — the props
 * carry an explicit `source: "router-host" | "lc-peer"` discriminator
 * + injected callbacks, so the same component renders both surfaces.
 *
 * Two subsections side-by-side under one section header:
 *   1. Not-yet-imported camera/NVR candidates (default-expanded) —
 *      each row exposes an icon-button "Add as camera/NVR" + a
 *      three-dots menu (designate / check / select). Jetson hosts
 *      get a quiet "no forward needed" hint instead of the button.
 *   2. Already-imported (default-collapsed) — green check chip + the
 *      bound camera name, no actions.
 *
 * Bulk mode is parent-controlled: caller toggles `selectionMode`, the
 * list paints rows with checkboxes + emits selection events. The
 * action-footer also lives in the parent so bulk semantics can differ
 * across router vs LC sources.
 */

/**
 * Normalised peer record — superset of `RouterProbeLanHost` for the
 * router source, and the shape LC can map onto once Group D adapts it.
 * Keep the field names aligned with both sources so the reducer is the
 * map-step, not a type translation layer.
 */
export interface DiscoveredPeer {
  /** Stable identity across re-renders. MAC is the safest pick. */
  mac: string;
  ip: string;
  host_name: string | null;
  active: boolean;
  /** Hostname-heuristic classification. `designation` overrides this
   *  visually when set. */
  kind: RouterProbeHostKind;
  /** Operator override fed back by the BE on the next probe. */
  designation?: RouterHostDesignation | null;
  /** Imported-as-camera annotation from Group A's probe payload. */
  imported_as_camera_id?: number | null;
  imported_as_camera_name?: string | null;
  imported_via_nvr_id?: number | null;
  /** Heuristic + per-host context only the parent knows. */
  is_jetson?: boolean;
  /** Existing RTSP-554 forward bound to this host, if any. Drives the
   *  confirm-modal copy in the import flow. */
  importable_forward?: RouterProbeForward | null;
  /** Non-RTSP forwards on the same host — display-only context. */
  other_forwards?: RouterProbeForward[];
}

export type DiscoveredPeerSource = 'router-host' | 'lc-peer';

export interface DiscoveredPeerListProps {
  /** Source discriminator — tweaks copy/affordances slightly. */
  source: DiscoveredPeerSource;
  /** Peers to render. Caller filters which ones are "candidates" vs
   *  "everything else" — both halves of the spec live in this list. */
  peers: DiscoveredPeer[];
  /** Section title, e.g. "Возможные камеры". Pass empty string to skip
   *  the internal header — useful when the caller's wrapper already
   *  carries the section title (collapsible <details> summary). */
  title: string;
  /** Optional help line under the title. */
  helpText?: string;

  // Bulk-select bridge — caller owns the state because it knows what
  // bulk actions are possible for the source.
  selectionMode: boolean;
  selectedMacs: Set<string>;
  onToggleSelectionMode: () => void;
  onToggleSelect: (mac: string) => void;

  // Per-row actions. Each is dispatched by clicking the row's primary
  // icon button — kind depends on the row's effective designation.
  onImport: (peer: DiscoveredPeer) => void;
  onDesignate: (peer: DiscoveredPeer, designation: RouterHostDesignation) => void;
  /** Optional reset-designation handler. When supplied AND the row has
   *  an operator-set designation, the three-dots menu surfaces a
   *  "Сбросить" item that clears the override (finding #14). */
  onResetDesignation?: (peer: DiscoveredPeer) => void;
  onCheck: (peer: DiscoveredPeer, kind: RouterCheckKind) => Promise<RouterCheckResponse>;

  /** Default-collapsed state for the "Уже добавлено" subsection. */
  defaultImportedOpen?: boolean;

  /** Drop down which kinds the three-dots menu can run as connectivity
   *  checks. Defaults to {ping, tcp_open, rtsp_stream} for camera/NVR
   *  rows and {ping, tcp_open} otherwise. Caller can suppress all
   *  checks for hosts without a forward (finding #21 follow-up) by
   *  passing an empty array. */
  checkKindsFor?: (peer: DiscoveredPeer) => readonly RouterCheckKind[];

  /** Optional already-imported map keyed on MAC. Drives the
   *  device-picker "уже импортирован" suffix elsewhere; here it lets
   *  the row's primary import affordance render disabled when the
   *  row's MAC already has a Camera. Default behaviour falls back to
   *  the peer's own `imported_as_camera_id` annotation. */
  importedMacIndex?: ReadonlySet<string>;
}

/**
 * Compute the effective host kind, after applying the operator
 * designation override + the parent-supplied jetson flag.
 *
 * Resolution order (highest → lowest):
 *   1. `designation` (operator-set, persistent)
 *   2. `is_jetson` flag (jetson → no actions, special note)
 *   3. `kind` (BE hostname heuristic)
 */
export type EffectiveKind =
  | 'camera'
  | 'nvr'
  | 'router'
  | 'jetson'
  | 'unknown'
  | 'other';

export function effectiveKind(peer: DiscoveredPeer): EffectiveKind {
  if (peer.designation) return peer.designation;
  if (peer.is_jetson) return 'jetson';
  if (peer.kind === 'camera') return 'camera';
  if (peer.kind === 'nvr') return 'nvr';
  if (peer.kind === 'likely_other') return 'other';
  return 'unknown';
}

/**
 * Display the host IP, hiding "0.0.0.0" placeholders that DHCP-less
 * Keenetic rows expose. Mirrors RouterAutodiscoverButton's existing
 * helper — duplicated here so this primitive has no upward import.
 */
function displayIp(peer: DiscoveredPeer): string {
  if (!peer.ip || peer.ip === '0.0.0.0' || !peer.active) return '—';
  return peer.ip;
}

export function DiscoveredPeerList({
  source: _source,
  peers,
  title,
  helpText,
  selectionMode,
  selectedMacs,
  onToggleSelectionMode,
  onToggleSelect,
  onImport,
  onDesignate,
  onResetDesignation,
  onCheck,
  defaultImportedOpen = false,
  checkKindsFor,
}: DiscoveredPeerListProps) {
  const { t } = useT();

  // Partition once. Imported rows are non-selectable; unimported rows
  // drive both the bulk-select footer counts and the primary import
  // affordance.
  const { unimported, imported } = useMemo(() => {
    const u: DiscoveredPeer[] = [];
    const i: DiscoveredPeer[] = [];
    for (const p of peers) {
      if (p.imported_as_camera_id) i.push(p);
      else u.push(p);
    }
    return { unimported: u, imported: i };
  }, [peers]);

  if (unimported.length === 0 && imported.length === 0) return null;

  return (
    <section className="space-y-2.5 text-sm" data-testid="discovered-peer-list">
      {(title || unimported.length > 0) && (
        <div className="flex items-baseline justify-between gap-2">
          {title ? (
            <SectionLabel>{title}</SectionLabel>
          ) : (
            <span aria-hidden />
          )}
          <div className="flex items-center gap-2 text-[11px] tabular-nums text-muted-foreground">
            {selectionMode && (
              <span>
                {[...selectedMacs].filter(m => unimported.some(p => p.mac === m)).length} / {unimported.length}
              </span>
            )}
            {unimported.length > 0 && (
              <button
                type="button"
                onClick={onToggleSelectionMode}
                className={cn(
                  'rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors',
                  selectionMode
                    ? 'bg-primary-muted text-primary-muted-foreground hover:bg-primary-muted/80'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
                data-testid="peer-list-bulk-toggle"
              >
                {selectionMode
                  ? t('equipment.discoveredPeer.bulkUnselect')
                  : t('equipment.discoveredPeer.bulkSelect')}
              </button>
            )}
          </div>
        </div>
      )}
      {helpText && (
        <p className="text-xs leading-relaxed text-muted-foreground">{helpText}</p>
      )}

      {unimported.length > 0 && (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card/40">
          {unimported.map(peer => (
            <PeerRow
              key={peer.mac}
              peer={peer}
              selectionMode={selectionMode}
              selected={selectedMacs.has(peer.mac)}
              onToggleSelect={() => onToggleSelect(peer.mac)}
              onImport={() => onImport(peer)}
              onDesignate={d => onDesignate(peer, d)}
              onResetDesignation={
                onResetDesignation ? () => onResetDesignation(peer) : undefined
              }
              onCheck={k => onCheck(peer, k)}
              checkKindsOverride={checkKindsFor ? checkKindsFor(peer) : undefined}
            />
          ))}
        </ul>
      )}

      {imported.length > 0 && (
        <details
          className="group rounded-lg border border-border bg-card/40"
          open={defaultImportedOpen}
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-[color:var(--color-success)]" aria-hidden />
              {t('equipment.router.sectionDevices.alreadyImported')}
              <span className="tabular-nums opacity-70">({imported.length})</span>
            </span>
            <ChevronDown className="size-3.5 transition-transform group-open:rotate-180" />
          </summary>
          <ul className="divide-y divide-border border-t border-border">
            {imported.map(peer => (
              <ImportedPeerRow key={peer.mac} peer={peer} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/**
 * Primary peer row. Layout (left → right):
 *
 *   [select-checkbox]?  ip · mac · hostname  ·  forward-badge  [actions]
 *
 * Actions cluster on the right:
 *   - icon button (Camera+ / Server+) — primary import path
 *   - three-dots menu — designate / connectivity / select toggle
 */
function PeerRow({
  peer,
  selectionMode,
  selected,
  onToggleSelect,
  onImport,
  onDesignate,
  onResetDesignation,
  onCheck,
  checkKindsOverride,
}: {
  peer: DiscoveredPeer;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onImport: () => void;
  onDesignate: (d: RouterHostDesignation) => void;
  onResetDesignation?: () => void;
  onCheck: (k: RouterCheckKind) => Promise<RouterCheckResponse>;
  checkKindsOverride?: readonly RouterCheckKind[];
}) {
  const { t } = useT();
  const kind = effectiveKind(peer);
  const ip = displayIp(peer);

  const isCamera = kind === 'camera';
  const isNvr = kind === 'nvr';
  const isRouter = kind === 'router';
  const isJetson = kind === 'jetson';
  // Router source: camera/NVR get import buttons (the page itself is
  // the router — no "Add as router" action here). LC source: camera,
  // NVR, AND router are all valid import targets — the LC peer list
  // can plant the zone's first router from a discovered host (Group D
  // "Добавить как роутер" requirement). Jetson hosts always render the
  // quiet "no forward needed" note instead of any import affordance.
  const showImport = !isJetson && (isCamera || isNvr || isRouter);

  const importLabel = isRouter
    ? t('equipment.discoveredPeer.import.router')
    : isNvr
      ? t('equipment.discoveredPeer.import.nvr')
      : t('equipment.discoveredPeer.import.camera');

  return (
    <li>
      <div
        className={cn(
          'flex min-h-[48px] flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 transition-colors',
          'hover:bg-muted/40',
          selected && 'bg-primary-muted/40 hover:bg-primary-muted/50',
        )}
      >
        {selectionMode && (
          <input
            type="checkbox"
            className="size-4 shrink-0 cursor-pointer rounded border-border accent-[color:var(--color-primary)]"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={peer.host_name || peer.mac}
            data-testid={`peer-row-${peer.mac}-select`}
          />
        )}
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-mono text-xs tabular-nums">{ip}</span>
          <span className="text-[10px] text-muted-foreground/60">·</span>
          <span className="truncate font-mono text-[11px] tabular-nums text-muted-foreground">
            {peer.mac}
          </span>
          {peer.host_name && (
            <>
              <span className="text-[10px] text-muted-foreground/60">·</span>
              <span className="truncate text-xs text-foreground/80">{peer.host_name}</span>
            </>
          )}
          {!peer.active && (
            <span className="text-[10px] text-muted-foreground">
              ({t('equipment.router.probe.offline')})
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {peer.importable_forward && (
            <Tooltip content={t('equipment.router.probe.forwardLabel')}><span
              className="inline-flex items-center gap-1 rounded-md bg-[color:var(--color-success)]/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-success)]"
            >
              <span className="size-1 rounded-full bg-[color:var(--color-success)]" aria-hidden />
              {t('equipment.router.probe.forwardLabel')}
              <span className="tabular-nums">
                {' '}{peer.importable_forward.external_port} → {peer.importable_forward.internal_port}
              </span>
            </span></Tooltip>
          )}
          {peer.other_forwards && peer.other_forwards.length > 0 && (
            peer.other_forwards.slice(0, 2).map((fwd, i) => (
              <span
                key={`${fwd.interface}:${fwd.external_port}:${i}`}
                className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
              >
                {(fwd.protocol || 'tcp').toUpperCase()}{' '}
                <span className="tabular-nums">{fwd.external_port} → {fwd.internal_port}</span>
              </span>
            ))
          )}

          {isJetson ? (
            <span className="text-[10px] italic text-muted-foreground">
              {t('equipment.discoveredPeer.jetsonNote')}
            </span>
          ) : showImport ? (
            <Tooltip content={importLabel}><button
              type="button"
              onClick={onImport}
              className={cn(
                'inline-flex size-7 items-center justify-center rounded-md border border-border bg-card/60 transition-colors',
                'text-foreground/80 hover:border-primary hover:bg-primary-muted hover:text-primary-muted-foreground',
              )}
              aria-label={importLabel}
              data-testid={`peer-row-${peer.mac}-import`}
            >
              {isNvr ? (
                <Server className="size-3.5" />
              ) : isRouter ? (
                <RouterIconLucide className="size-3.5" />
              ) : (
                <span className="relative inline-flex items-center justify-center">
                  <CameraIcon className="size-3.5" />
                  <PlusIcon className="absolute -right-1 -top-1 size-2.5 rounded-full bg-card text-[color:var(--color-success)]" />
                </span>
              )}
            </button></Tooltip>
          ) : null}

          <HostDesignationMenu
            hostKey={peer.mac}
            allowedDesignations={['camera', 'nvr', 'router']}
            checkKinds={
              checkKindsOverride ??
              (isCamera || isNvr ? ['ping', 'tcp_open', 'rtsp_stream'] : ['ping', 'tcp_open'])
            }
            bulkActive={selectionMode}
            bulkSelected={selected}
            currentDesignation={peer.designation ?? null}
            onToggleBulk={onToggleSelect}
            onDesignate={onDesignate}
            onResetDesignation={onResetDesignation}
            onCheck={onCheck}
          />
        </div>
      </div>
    </li>
  );
}

/**
 * Already-imported row. Green check chip + camera name; no actions —
 * the operator manages these from the Cameras tab now.
 */
function ImportedPeerRow({ peer }: { peer: DiscoveredPeer }) {
  const { t } = useT();
  const ip = displayIp(peer);
  return (
    <li className="flex min-h-[40px] flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs">
      <span
        className="inline-flex items-center gap-1 rounded-md bg-[color:var(--color-success)]/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-success)]"
        data-testid="peer-imported-chip"
      >
        <CheckCircle2 className="size-3" aria-hidden />
        {t('equipment.discoveredPeer.alreadyImported')}
      </span>
      <span className="font-mono tabular-nums">{ip}</span>
      <span className="text-muted-foreground/60">·</span>
      <span className="truncate font-mono tabular-nums text-muted-foreground">{peer.mac}</span>
      {peer.imported_as_camera_name && (
        <>
          <span className="text-muted-foreground/60">·</span>
          <span className="truncate text-foreground/90">«{peer.imported_as_camera_name}»</span>
        </>
      )}
      {peer.imported_via_nvr_id && (
        <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {t('equipment.discoveredPeer.viaNvr')}
        </span>
      )}
    </li>
  );
}

/**
 * Bulk-mode action footer. Lives outside the list itself so the parent
 * can pick which bulk actions are exposed (router → both; LC → only
 * import-cameras). Renders nothing when nothing's selected.
 */
export function DiscoveredPeerBulkFooter({
  selectedCount,
  onCancel,
  onImportCameras,
  onCreateForwards,
  loading = false,
}: {
  selectedCount: number;
  onCancel: () => void;
  onImportCameras?: () => void;
  onCreateForwards?: () => void;
  loading?: boolean;
}) {
  const { t } = useT();
  if (selectedCount === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-primary-muted/40 px-3 py-2"
      data-testid="peer-bulk-footer"
    >
      <span className="text-xs text-muted-foreground">
        {t('equipment.discoveredPeer.bulk.footer').replace('{{count}}', String(selectedCount))}
      </span>
      <div className="flex items-center gap-1.5">
        {onCreateForwards && (
          <button
            type="button"
            onClick={onCreateForwards}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground/90 transition-colors hover:bg-muted disabled:cursor-wait disabled:opacity-60"
          >
            {loading && <Loader2 className="size-3 animate-spin" />}
            {t('equipment.discoveredPeer.bulk.createForwards')}
          </button>
        )}
        {onImportCameras && (
          <button
            type="button"
            onClick={onImportCameras}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-wait disabled:opacity-60"
            data-testid="peer-bulk-import"
          >
            {loading && <Loader2 className="size-3 animate-spin" />}
            {t('equipment.discoveredPeer.bulk.importCameras')}
            <span className="ml-1 rounded bg-white/20 px-1 text-[10px] font-bold tabular-nums">
              {selectedCount}
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="inline-flex items-center rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed"
        >
          {t('equipment.discoveredPeer.bulk.cancel')}
        </button>
      </div>
    </div>
  );
}

export default DiscoveredPeerList;
